import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const CommissionBreakdown = ({ transactions, onDisputeSubmit }) => {
  const [expandedTransaction, setExpandedTransaction] = useState(null);
  const [showDisputeModal, setShowDisputeModal] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [disputeReason, setDisputeReason] = useState('');

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-success/10 text-success border-success/20';
      case 'pending':
        return 'bg-warning/10 text-warning border-warning/20';
      case 'disputed':
        return 'bg-error/10 text-error border-error/20';
      default:
        return 'bg-muted/10 text-muted-foreground border-muted/20';
    }
  };

  const handleExpandToggle = (transactionId) => {
    setExpandedTransaction(expandedTransaction === transactionId ? null : transactionId);
  };

  const handleDisputeClick = (transaction) => {
    setSelectedTransaction(transaction);
    setShowDisputeModal(true);
  };

  const handleDisputeSubmit = () => {
    if (selectedTransaction && disputeReason) {
      onDisputeSubmit(selectedTransaction?.id, disputeReason);
      setShowDisputeModal(false);
      setSelectedTransaction(null);
      setDisputeReason('');
    }
  };

  const calculateCommissionBreakdown = (transaction) => {
    const baseCommission = (transaction?.transactionAmount * transaction?.commissionRate) / 100;
    const bonusCommission = baseCommission * 0.1; // 10% bonus
    const deductions = baseCommission * 0.05; // 5% processing fee
    const netCommission = baseCommission + bonusCommission - deductions;

    return {
      baseCommission,
      bonusCommission,
      deductions,
      netCommission
    };
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-foreground">Commission Breakdown</h3>
        <div className="flex items-center space-x-2 text-sm text-muted-foreground">
          <Icon name="Info" size={16} />
          <span>Click to expand calculation details</span>
        </div>
      </div>
      <div className="space-y-4">
        {transactions?.map((transaction) => {
          const breakdown = calculateCommissionBreakdown(transaction);
          const isExpanded = expandedTransaction === transaction?.id;

          return (
            <div
              key={transaction?.id}
              className="border border-border rounded-lg p-4 hover:border-primary/20 transition-colors"
            >
              {/* Transaction Header */}
              <div
                className="flex items-center justify-between cursor-pointer"
                onClick={() => handleExpandToggle(transaction?.id)}
              >
                <div className="flex items-center space-x-4">
                  <div className="flex items-center space-x-2">
                    <Icon name="ArrowRightLeft" size={16} className="text-primary" />
                    <span className="font-medium text-foreground">{transaction?.id}</span>
                  </div>
                  <span className="text-sm text-muted-foreground">
                    {new Date(transaction?.date)?.toLocaleDateString()}
                  </span>
                  <span className={`text-xs px-2 py-1 rounded-full border ${getStatusColor(transaction?.status)}`}>
                    {transaction?.status}
                  </span>
                </div>
                <div className="flex items-center space-x-4">
                  <div className="text-right">
                    <p className="text-sm text-muted-foreground">Commission</p>
                    <p className="font-semibold text-foreground">
                      ${breakdown?.netCommission?.toFixed(2)}
                    </p>
                  </div>
                  <Icon 
                    name={isExpanded ? "ChevronUp" : "ChevronDown"} 
                    size={16} 
                    className="text-muted-foreground"
                  />
                </div>
              </div>
              {/* Expanded Details */}
              {isExpanded && (
                <div className="mt-4 pt-4 border-t border-border">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Transaction Info */}
                    <div className="space-y-3">
                      <h4 className="font-medium text-foreground">Transaction Details</h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Customer:</span>
                          <span className="text-foreground">{transaction?.customerName}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Amount:</span>
                          <span className="text-foreground">
                            ${transaction?.transactionAmount?.toLocaleString()}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Territory:</span>
                          <span className="text-foreground">{transaction?.territory}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Type:</span>
                          <span className="text-foreground">{transaction?.type}</span>
                        </div>
                      </div>
                    </div>

                    {/* Commission Calculation */}
                    <div className="space-y-3">
                      <h4 className="font-medium text-foreground">Commission Calculation</h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">
                            Base ({transaction?.commissionRate}%):
                          </span>
                          <span className="text-foreground">
                            ${breakdown?.baseCommission?.toFixed(2)}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-success">Performance Bonus:</span>
                          <span className="text-success">
                            +${breakdown?.bonusCommission?.toFixed(2)}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-error">Processing Fee:</span>
                          <span className="text-error">
                            -${breakdown?.deductions?.toFixed(2)}
                          </span>
                        </div>
                        <div className="flex justify-between pt-2 border-t border-border font-medium">
                          <span className="text-foreground">Net Commission:</span>
                          <span className="text-primary">
                            ${breakdown?.netCommission?.toFixed(2)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="mt-4 pt-4 border-t border-border flex justify-end space-x-3">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDisputeClick(transaction)}
                      iconName="AlertTriangle"
                      iconPosition="left"
                      iconSize={14}
                    >
                      Dispute
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="Download"
                      iconPosition="left"
                      iconSize={14}
                    >
                      Statement
                    </Button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
      {/* Dispute Modal */}
      {showDisputeModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-card border border-border rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-foreground">Submit Dispute</h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowDisputeModal(false)}
                iconName="X"
                iconSize={16}
              />
            </div>
            
            <div className="space-y-4">
              <div>
                <p className="text-sm text-muted-foreground mb-2">
                  Transaction: {selectedTransaction?.id}
                </p>
                <p className="text-sm text-muted-foreground">
                  Commission: ${calculateCommissionBreakdown(selectedTransaction)?.netCommission?.toFixed(2)}
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Dispute Reason
                </label>
                <textarea
                  value={disputeReason}
                  onChange={(e) => setDisputeReason(e?.target?.value)}
                  placeholder="Please explain the reason for this dispute..."
                  className="w-full h-24 px-3 py-2 border border-border rounded-md bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>
              
              <div className="flex justify-end space-x-3">
                <Button
                  variant="outline"
                  onClick={() => setShowDisputeModal(false)}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleDisputeSubmit}
                  disabled={!disputeReason?.trim()}
                >
                  Submit Dispute
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CommissionBreakdown;